import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Circle,
  Footprints,
  Play,
  Sparkles,
  Square,
  Sprout,
} from "lucide-react";

import { ProfileOverviewHeader } from "@/components/panel/ProfileOverviewHeader";

import { Input } from "@/components/ui/input";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { getDailyGuidance } from "@/lib/karma/daily";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { cn } from "@/lib/utils";
import { sendProgressEvent } from "@/lib/progress";
import { createSocialPost } from "@/lib/supabase/social";
import { listCircleTasksForMe, listMyCircles, toggleCircleTaskCompletionInDb } from "@/lib/supabase/circles";

type ActivityDifficulty = "easy" | "medium" | "hard";

type WellnessActivity = {
  id: string;
  title: string;
  description: string;
  iconKey?: string;
  metaLabel?: string;
  metaValue?: string;
  durationMinutes?: number;
  difficulty?: ActivityDifficulty;
  coverImageUrl?: string;
  audioUrl?: string;
  featured?: boolean;
  active: boolean;
  tags: string[];
};

type ProgramItem = {
  id: string;
  title: string;
  description?: string;
  tags: string[];
  iconKey?: string;
  coverImageUrl?: string;
  audioUrl?: string;
  durationMinutes?: number;
  active?: boolean;
};

type ActivitiesResponse = {
  ok: true;
  items: WellnessActivity[];
};

type ProgramItemsResponse = {
  ok: true;
  items: ProgramItem[];
};

type ShopProduct = {
  id: string;
  name: string;
  priceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  imageUrl?: string;
};

type ProductsResponse = {
  ok: true;
  products: ShopProduct[];
};

async function fetchActivities(): Promise<WellnessActivity[]> {
  const res = await fetch("/api/numeroloji/activities");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Aktiviteler alınamadı.");
  }
  const data = json as ActivitiesResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

async function fetchProducts(): Promise<ShopProduct[]> {
  const res = await fetch("/api/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ürünler alınamadı.");
  }
  const data = json as ProductsResponse;
  return Array.isArray(data?.products) ? data.products : [];
}

async function fetchProgramItems(path: string): Promise<ProgramItem[]> {
  const res = await fetch(path);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Program verisi alınamadı.");
  }
  const data = json as ProgramItemsResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

async function fetchMeditationPrograms() {
  return fetchProgramItems("/api/numeroloji/meditations");
}

async function fetchBreathPrograms() {
  return fetchProgramItems("/api/numeroloji/breaths");
}

async function fetchDietPrograms() {
  return fetchProgramItems("/api/numeroloji/diets");
}

function normalizeTag(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function toPublicUrl(rawValue?: string) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("data:") || raw.startsWith("blob:") || raw.startsWith("/")) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  if (raw.startsWith("ftp://")) return raw.replace(/^ftp:\/\//i, "https://");
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `https://${raw}`;
}

function getKarmicDebtTag(code: number) {
  return `karmikborc${code}`;
}

function hasRituelTag(tags: string[]) {
  const values = new Set((tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean));
  return values.has("rituel") || values.has("ritueluygulamasi") || values.has("ritual");
}

const RITUAL_TAG_PREFIX = {
  intent: "rituel_niyet:",
  material: "rituel_malzeme:",
  step: "rituel_adim:",
  points: "rituel_puan:",
  programDays: "rituel_program_gun:",
  product: "rituel_urun:",
} as const;

function decodeTagValue(raw: string) {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function parseRituelConfigFromTags(tags: string[]) {
  const intentRaw =
    tags
      .find((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.intent))
      ?.slice(RITUAL_TAG_PREFIX.intent.length) ?? "";

  const pointsRaw =
    tags
      .find((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.points))
      ?.slice(RITUAL_TAG_PREFIX.points.length) ?? "";

  const programDaysRaw =
    tags
      .find((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.programDays))
      ?.slice(RITUAL_TAG_PREFIX.programDays.length) ?? "";

  const materials = tags
    .filter((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.material))
    .map((tag) => decodeTagValue(String(tag).slice(RITUAL_TAG_PREFIX.material.length)))
    .filter(Boolean);

  const steps = tags
    .filter((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.step))
    .map((tag) => decodeTagValue(String(tag).slice(RITUAL_TAG_PREFIX.step.length)))
    .filter(Boolean);

  const productIds = tags
    .filter((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.product))
    .map((tag) => String(tag).slice(RITUAL_TAG_PREFIX.product.length).trim())
    .filter(Boolean);

  const parsedPoints = Number(pointsRaw);
  const parsedProgramDays = Number(programDaysRaw);
  return {
    intent: decodeTagValue(intentRaw),
    materials,
    steps,
    productIds: Array.from(new Set(productIds)),
    programDays: Number.isFinite(parsedProgramDays) && parsedProgramDays > 0 ? Math.round(parsedProgramDays) : 0,
    points: Number.isFinite(parsedPoints) && parsedPoints > 0 ? Math.round(parsedPoints) : 0,
  };
}

function difficultyLabel(diff?: ActivityDifficulty) {
  if (diff === "hard") return "Zor";
  if (diff === "medium") return "Orta";
  return "Kolay";
}

type TaskGroupKey = "diyet" | "meditasyon" | "nefes" | "rituel" | "cember";

type TodayTaskItem = {
  id: string;
  title: string;
  description: string;
  coverImageUrl?: string;
  audioUrl?: string;
  group: TaskGroupKey;
  targetId: string;
  playable: boolean;
  audioRequired: boolean;
  started: boolean;
  done: boolean;
  circleName?: string;
  circleTaskKind?: TaskGroupKey;
  circleId?: string;
  circleTaskId?: string;
  points?: number;
};

type CircleDailyTaskItem = {
  id: string;
  rawTaskId: string;
  circleId: string;
  circleName: string;
  title: string;
  description: string;
  kind: TaskGroupKey;
  completed: boolean;
  points: number;
};

const TASK_GROUP_LABELS: Record<TaskGroupKey, string> = {
  diyet: "Diyet Görevleri",
  meditasyon: "Meditasyon Görevleri",
  nefes: "Nefes Görevleri",
  rituel: "Ritüel Görevleri",
  cember: "Çember Görevleri",
};

const TASK_GROUP_ORDER: TaskGroupKey[] = ["diyet", "meditasyon", "nefes", "rituel", "cember"];
const ACTIVITY_TASK_POINTS_PREFIXES = ["gorev_puan:", "task_puan:", "diyet_gorev_puan:"] as const;

function mapCircleTaskPointsToXp(points: number) {
  const numeric = Math.max(0, Math.round(Number(points) || 0));
  if (numeric >= 3) return 50;
  if (numeric >= 2) return 30;
  return 15;
}

function parsePositiveNumberTag(tags: string[], prefixes: readonly string[]) {
  const raw =
    (tags ?? [])
      .map((tag) => String(tag ?? "").trim())
      .find((tag) => prefixes.some((prefix) => tag.startsWith(prefix))) ?? "";

  const prefix = prefixes.find((item) => raw.startsWith(item));
  if (!prefix) return 0;

  const parsed = Number(raw.slice(prefix.length));
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function parseProgramDaysFromTags(tags: string[]) {
  let best = 0;
  for (const tag of tags ?? []) {
    const raw = String(tag ?? "").trim();
    const match = raw.match(/^program_gun\s*:\s*(\d{1,3})$/i);
    const parsed = Number(match?.[1] ?? "");
    if (Number.isFinite(parsed) && parsed > best) {
      best = Math.round(parsed);
    }
  }
  return best;
}

function parseRitualProgramDaysFromTags(tags: string[]) {
  const raw = (tags ?? []).find((tag) => String(tag ?? "").startsWith("rituel_program_gun:"))?.slice("rituel_program_gun:".length) ?? "";
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function resolveProgramDaysFromItem(item: ProgramItem) {
  const fromTags = parseProgramDaysFromTags(item.tags ?? []);
  if (fromTags > 0) return fromTags;

  const text = `${item.title} ${item.description ?? ""} ${(item.tags ?? []).join(" ")}`;
  const match = text.match(/(\d{1,3})\s*g[uü]n/i);
  const parsed = Number(match?.[1] ?? "");
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function parseActivityProgramDay(tags: string[]) {
  const explicit = parseProgramDaysFromTags(tags);
  if (explicit > 0) return explicit;
  const ritual = parseRitualProgramDaysFromTags(tags);
  if (ritual > 0) return ritual;

  for (const rawTag of tags ?? []) {
    const tag = String(rawTag ?? "").trim().toLocaleLowerCase("tr-TR");
    if (!tag.includes("program_gun:")) continue;
    const parsed = Number(tag.split(":").pop() ?? "");
    if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed);
  }
  return 0;
}

function parseActivityTargetId(tags: string[], group: TaskGroupKey) {
  const rawTags = (tags ?? []).map((tag) => String(tag ?? "").trim());
  const explicitTarget = rawTags.find((tag) => tag.toLocaleLowerCase("tr-TR").startsWith("gorev_hedef:"));
  if (explicitTarget) {
    return explicitTarget.split(":").slice(1).join(":").trim();
  }

  const aliases: Record<TaskGroupKey, string[]> = {
    diyet: ["diyet:", "diet:"],
    meditasyon: ["meditasyon:", "meditation:"],
    nefes: ["nefes:", "breath:"],
    rituel: ["rituel:", "ritual:"],
    cember: ["cember:", "circle:"],
  };

  for (const prefix of aliases[group]) {
    const found = rawTags.find((tag) => tag.toLocaleLowerCase("tr-TR").startsWith(prefix));
    if (found) return found.split(":").slice(1).join(":").trim();
  }

  return "";
}

function resolveTaskGroup(activity: WellnessActivity): TaskGroupKey | null {
  const rawTags = (activity.tags ?? []).map((tag) => String(tag ?? "").trim());
  const normalizedTagKeys = new Set(rawTags.map((tag) => normalizeTag(tag)).filter(Boolean));
  const lowerRawTags = rawTags.map((tag) => tag.toLocaleLowerCase("tr-TR"));
  const iconKey = normalizeTag(activity.iconKey ?? "");

  if (hasRituelTag(activity.tags ?? []) || lowerRawTags.includes("gorev_tur:rituel") || lowerRawTags.includes("gorev_tur:ritual")) {
    return "rituel";
  }

  if (lowerRawTags.includes("gorev_tur:cember") || normalizedTagKeys.has("cember") || normalizedTagKeys.has("circle") || normalizedTagKeys.has("cembergorevi")) {
    return "cember";
  }

  if (
    lowerRawTags.includes("gorev_tur:diyet") ||
    lowerRawTags.includes("gorev_tur:diet") ||
    normalizedTagKeys.has("diyet") ||
    normalizedTagKeys.has("diet") ||
    normalizedTagKeys.has("beslenme") ||
    iconKey === "nutrition"
  ) {
    return "diyet";
  }

  const textHaystack = `${activity.title} ${activity.description} ${rawTags.join(" ")}`.toLocaleLowerCase("tr-TR");

  if (
    lowerRawTags.includes("gorev_tur:meditasyon") ||
    lowerRawTags.includes("gorev_tur:meditation") ||
    normalizedTagKeys.has("meditasyon") ||
    normalizedTagKeys.has("meditation") ||
    iconKey === "meditation" ||
    textHaystack.includes("meditasyon")
  ) {
    return "meditasyon";
  }

  if (
    lowerRawTags.includes("gorev_tur:nefes") ||
    lowerRawTags.includes("gorev_tur:breath") ||
    normalizedTagKeys.has("nefes") ||
    normalizedTagKeys.has("breath") ||
    iconKey === "breath" ||
    textHaystack.includes("nefes")
  ) {
    return "nefes";
  }

  return null;
}

function parseActivityTaskPoints(activity: WellnessActivity) {
  const ritualPoints = parsePositiveNumberTag(activity.tags ?? [], [RITUAL_TAG_PREFIX.points]);
  if (ritualPoints > 0) return ritualPoints;

  const taggedPoints = parsePositiveNumberTag(activity.tags ?? [], ACTIVITY_TASK_POINTS_PREFIXES);
  if (taggedPoints > 0) return taggedPoints;

  if (activity.difficulty === "hard") return 5;
  if (activity.difficulty === "medium") return 4;
  return 3;
}

function isPlayableTask(activity: WellnessActivity) {
  const iconKey = normalizeTag(activity.iconKey ?? "");
  const text = `${activity.title} ${activity.description} ${(activity.tags ?? []).join(" ")}`.toLocaleLowerCase("tr-TR");
  return (
    iconKey === "meditation" ||
    iconKey === "breath" ||
    text.includes("meditasyon") ||
    text.includes("nefes") ||
    text.includes("dinle") ||
    text.includes("ses")
  );
}

function extractImageFromTaskDescription(description: string) {
  const lines = String(description ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const imageLine = lines.find((line) => line.toLocaleLowerCase("tr-TR").startsWith("görsel:"));
  if (!imageLine) return "";
  return toPublicUrl(imageLine.slice("görsel:".length).trim());
}

function findProgramByTitle(items: ProgramItem[], title: string) {
  const target = normalizeTag(title);
  if (!target) return null;
  return (
    items.find((item) => normalizeTag(item.title) === target) ??
    items.find((item) => normalizeTag(item.title).includes(target) || target.includes(normalizeTag(item.title))) ??
    null
  );
}

function parseTaskDescriptionForSteps(description: string) {
  const lines = String(description ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !line.toLocaleLowerCase("tr-TR").startsWith("plan:"))
    .filter((line) => !line.toLocaleLowerCase("tr-TR").startsWith("görsel:"));

  if (!lines.length) return { summary: "", steps: [] as string[] };

  if (lines.length > 1) {
    return { summary: lines[0], steps: lines.slice(1) };
  }

  const single = lines[0];
  if (!/\d+\./.test(single)) {
    return { summary: single, steps: [] as string[] };
  }

  const normalized = single.replace(/\s+/g, " ").trim();
  const matches = normalized.match(/\d+\.\s*[^\d]+(?=(\s+\d+\.|$))/g) ?? [];
  const steps = matches.map((item) => item.replace(/^\d+\.\s*/, "").trim()).filter(Boolean);
  return steps.length ? { summary: "", steps } : { summary: single, steps: [] as string[] };
}

type ActivityAccent = "orange" | "blue" | "green";

type ActivityVisual = {
  accent: ActivityAccent;
  icon: (props: { className?: string }) => ReactNode;
};

function getActivityVisual(iconKey?: string): ActivityVisual {
  const key = String(iconKey ?? "").toLocaleLowerCase("tr-TR").trim();
  if (key === "meditation") {
    return {
      accent: "orange",
      icon: ({ className }) => <Sparkles className={className} />,
    };
  }

  if (key === "learning") {
    return {
      accent: "blue",
      icon: ({ className }) => <BookOpen className={className} />,
    };
  }

  if (key === "nutrition") {
    return {
      accent: "green",
      icon: ({ className }) => <Sprout className={className} />,
    };
  }

  return {
    accent: "blue",
    icon: ({ className }) => <Footprints className={className} />,
  };
}

function AccentIcon({
  accent,
  children,
}: {
  accent: ActivityAccent;
  children: ReactNode;
}) {
  const style =
    accent === "orange"
      ? "bg-orange-500/10 text-orange-500"
      : accent === "blue"
        ? "bg-blue-500/10 text-blue-500"
        : "bg-emerald-500/10 text-emerald-500";

  return <div className={cn("grid size-16 place-items-center rounded-2xl", style)}>{children}</div>;
}

const ACTIVITY_PROGRESS_KEY = "numeroloji_activity_progress_v1";
const RITUAL_REWARD_KEY = "numeroloji_rituel_reward_v1";
const PROGRAM_DAY_TRACKING_KEY = "numeroloji_program_day_tracking_v1";

type ActivityProgressState = {
  completedByDate: Record<string, string[]>;
};

type ActivityProgressStore = Record<string, ActivityProgressState>;

type RitualRewardState = {
  rewardsByDate: Record<string, Record<string, number>>;
};

type RitualRewardStore = Record<string, RitualRewardState>;

type ProgramDayTrackingState = {
  startedAtByItemId: Record<string, string>;
};

type ProgramDayTrackingStore = Record<string, ProgramDayTrackingState>;

function formatLocalDateKey(d: Date) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function loadActivityProgress(userKey: string): ActivityProgressState {
  if (typeof window === "undefined") return { completedByDate: {} };

  try {
    const raw = window.localStorage.getItem(ACTIVITY_PROGRESS_KEY);
    if (!raw) return { completedByDate: {} };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { completedByDate: {} };

    const store = parsed as ActivityProgressStore;
    const state = store[userKey];
    if (!state || typeof state !== "object") return { completedByDate: {} };

    return {
      completedByDate:
        state.completedByDate && typeof state.completedByDate === "object" ? (state.completedByDate as Record<string, string[]>) : {},
    };
  } catch {
    return { completedByDate: {} };
  }
}

function saveActivityProgress(userKey: string, state: ActivityProgressState) {
  if (typeof window === "undefined") return;

  try {
    const raw = window.localStorage.getItem(ACTIVITY_PROGRESS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    const base = parsed && typeof parsed === "object" ? (parsed as ActivityProgressStore) : {};

    const next: ActivityProgressStore = {
      ...base,
      [userKey]: state,
    };

    window.localStorage.setItem(ACTIVITY_PROGRESS_KEY, JSON.stringify(next));
  } catch {
    // ignore local storage errors
  }
}

function loadRitualRewards(userKey: string): RitualRewardState {
  if (typeof window === "undefined") return { rewardsByDate: {} };

  try {
    const raw = window.localStorage.getItem(RITUAL_REWARD_KEY);
    if (!raw) return { rewardsByDate: {} };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { rewardsByDate: {} };

    const store = parsed as RitualRewardStore;
    const state = store[userKey];
    if (!state || typeof state !== "object") return { rewardsByDate: {} };

    return {
      rewardsByDate:
        state.rewardsByDate && typeof state.rewardsByDate === "object"
          ? (state.rewardsByDate as Record<string, Record<string, number>>)
          : {},
    };
  } catch {
    return { rewardsByDate: {} };
  }
}

function saveRitualRewards(userKey: string, state: RitualRewardState) {
  if (typeof window === "undefined") return;

  try {
    const raw = window.localStorage.getItem(RITUAL_REWARD_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    const base = parsed && typeof parsed === "object" ? (parsed as RitualRewardStore) : {};

    const next: RitualRewardStore = {
      ...base,
      [userKey]: state,
    };

    window.localStorage.setItem(RITUAL_REWARD_KEY, JSON.stringify(next));
  } catch {
    // ignore local storage errors
  }
}

function loadProgramDayTracking(userKey: string): ProgramDayTrackingState {
  if (typeof window === "undefined") return { startedAtByItemId: {} };

  try {
    const raw = window.localStorage.getItem(PROGRAM_DAY_TRACKING_KEY);
    if (!raw) return { startedAtByItemId: {} };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { startedAtByItemId: {} };

    const store = parsed as ProgramDayTrackingStore;
    const state = store[userKey];
    if (!state || typeof state !== "object") return { startedAtByItemId: {} };

    return {
      startedAtByItemId:
        state.startedAtByItemId && typeof state.startedAtByItemId === "object"
          ? (state.startedAtByItemId as Record<string, string>)
          : {},
    };
  } catch {
    return { startedAtByItemId: {} };
  }
}

function saveProgramDayTracking(userKey: string, state: ProgramDayTrackingState) {
  if (typeof window === "undefined") return;

  try {
    const raw = window.localStorage.getItem(PROGRAM_DAY_TRACKING_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    const base = parsed && typeof parsed === "object" ? (parsed as ProgramDayTrackingStore) : {};

    const next: ProgramDayTrackingStore = {
      ...base,
      [userKey]: state,
    };

    window.localStorage.setItem(PROGRAM_DAY_TRACKING_KEY, JSON.stringify(next));
  } catch {
    // ignore local storage errors
  }
}

function resolveProgramDay(startedDateKey: string | undefined, todayKey: string) {
  if (!startedDateKey) return 0;
  const start = new Date(`${startedDateKey}T00:00:00`);
  const today = new Date(`${todayKey}T00:00:00`);
  const startMs = start.getTime();
  const todayMs = today.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(todayMs) || todayMs < startMs) return 1;
  return Math.floor((todayMs - startMs) / 86_400_000) + 1;
}

export default function UserPanelAktivite() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isRituelMode = pathname.startsWith("/panel/rituel");
  const { user } = useSupabaseSession();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const daily = useMemo(() => getDailyGuidance(new Date()), []);
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const audioPlaybackRef = useRef<Record<string, { durationSeconds: number; listenedSeconds: number; lastCurrentTime: number; lastWallTime: number }>>({});

  const userKey = user?.id ? `sb:${user.id}` : "anon";
  const [progress, setProgress] = useState<ActivityProgressState>(() => loadActivityProgress(userKey));
  const [ritualRewards, setRitualRewards] = useState<RitualRewardState>(() => loadRitualRewards(userKey));
  const [programDayTracking, setProgramDayTracking] = useState<ProgramDayTrackingState>(() => loadProgramDayTracking(userKey));

  useEffect(() => {
    setProgress(loadActivityProgress(userKey));
    setRitualRewards(loadRitualRewards(userKey));
    setProgramDayTracking(loadProgramDayTracking(userKey));
  }, [userKey]);

  const toggleCompleteToday = useCallback(
    (activityId: string) => {
      const todayKey = formatLocalDateKey(new Date());

      setProgress((prev) => {
        const current = prev.completedByDate[todayKey] ?? [];
        const exists = current.includes(activityId);
        const nextList = exists ? current.filter((id) => id !== activityId) : [...current, activityId];

        const next: ActivityProgressState = {
          completedByDate: {
            ...prev.completedByDate,
            [todayKey]: nextList,
          },
        };

        saveActivityProgress(userKey, next);
        return next;
      });
    },
    [userKey],
  );

  const profileInput = useMemo(() => {
    const firstName = typeof user?.metadata?.first_name === "string" ? (user?.metadata?.first_name as string) : "";
    const lastName = typeof user?.metadata?.last_name === "string" ? (user?.metadata?.last_name as string) : "";
    const fullName = `${firstName} ${lastName}`.trim();

    const birthDay = typeof user?.metadata?.birth_day === "number" ? user?.metadata?.birth_day : Number(user?.metadata?.birth_day);
    const birthMonth =
      typeof user?.metadata?.birth_month === "number" ? user?.metadata?.birth_month : Number(user?.metadata?.birth_month);
    const birthYear = typeof user?.metadata?.birth_year === "number" ? user?.metadata?.birth_year : Number(user?.metadata?.birth_year);

    if (!fullName || !Number.isFinite(birthDay) || !Number.isFinite(birthMonth) || !Number.isFinite(birthYear)) return null;

    const birthDate = parseBirthDate({
      day: birthDay,
      month: birthMonth,
      year: birthYear,
    });

    return {
      fullName,
      birthDate,
    };
  }, [
    user?.metadata?.birth_day,
    user?.metadata?.birth_month,
    user?.metadata?.birth_year,
    user?.metadata?.first_name,
    user?.metadata?.last_name,
  ]);

  const numerology = useMemo(() => {
    if (!profileInput) return null;
    return computeKarmaNumerology(profileInput.fullName, profileInput.birthDate);
  }, [profileInput]);

  const karmicCodes = numerology?.karmicCodes ?? [];

  const activitiesQuery = useQuery({
    queryKey: ["panel-activities"],
    queryFn: fetchActivities,
    staleTime: 30_000,
  });

  const productsQuery = useQuery({
    queryKey: ["panel-products-catalog"],
    queryFn: fetchProducts,
    enabled: isRituelMode,
    staleTime: 30_000,
  });

  const meditationProgramsQuery = useQuery({
    queryKey: ["panel-meditation-programs"],
    queryFn: fetchMeditationPrograms,
    staleTime: 60_000,
  });

  const breathProgramsQuery = useQuery({
    queryKey: ["panel-breath-programs"],
    queryFn: fetchBreathPrograms,
    staleTime: 60_000,
  });

  const dietProgramsQuery = useQuery({
    queryKey: ["panel-diet-programs"],
    queryFn: fetchDietPrograms,
    staleTime: 60_000,
  });

  const circleTasksQuery = useQuery({
    queryKey: ["panel-activity-circle-tasks", user?.id],
    enabled: Boolean(user?.id),
    staleTime: 20_000,
    queryFn: async (): Promise<CircleDailyTaskItem[]> => {
      const circles = await listMyCircles(String(user?.id ?? ""));
      const rows = await Promise.all(
        circles.map(async (circle) => {
          const tasks = await listCircleTasksForMe(circle.id).catch(() => []);
          return tasks
            .filter((task) => task.active !== false)
            .map((task) => ({
              id: `cember:${circle.id}:${task.id}`,
              rawTaskId: task.id,
              circleId: circle.id,
              circleName: circle.name,
              title: task.title,
              description: task.description?.trim() || `${circle.name} çember görevi`,
              kind: task.kind === "meditasyon" || task.kind === "nefes" || task.kind === "rituel" || task.kind === "diyet" ? task.kind : "cember",
              completed: Boolean(task.completed),
              points: Number.isFinite(Number(task.points)) ? Number(task.points) : 1,
            }));
        }),
      );

      return rows.flat();
    },
  });

  const activityPool = useMemo(() => {
    const base = (activitiesQuery.data ?? []).filter((item) => item && item.active);
    const existingIds = new Set(base.map((item) => item.id));

    const meditationVirtualItems = (meditationProgramsQuery.data ?? [])
      .filter((item) => item && (item.active ?? true))
      .filter((item) => !existingIds.has(item.id))
      .map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description?.trim() || "Meditasyon programını başlatıp günlük adımı burada tamamlayabilirsiniz.",
        iconKey: "meditation",
        coverImageUrl: item.coverImageUrl,
        audioUrl: item.audioUrl,
        active: true,
        tags: Array.from(new Set([...(item.tags ?? []), "gorev_tur:meditasyon", `gorev_hedef:${item.id}`, `meditasyon:${item.id}`])),
      })) as WellnessActivity[];

    const breathVirtualItems = (breathProgramsQuery.data ?? [])
      .filter((item) => item && (item.active ?? true))
      .filter((item) => !existingIds.has(item.id))
      .map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description?.trim() || "Nefes programını başlatıp günlük adımı burada tamamlayabilirsiniz.",
        iconKey: "breath",
        coverImageUrl: item.coverImageUrl,
        audioUrl: item.audioUrl,
        active: true,
        tags: Array.from(new Set([...(item.tags ?? []), "gorev_tur:nefes", `gorev_hedef:${item.id}`, `nefes:${item.id}`])),
      })) as WellnessActivity[];

    return [...base, ...meditationVirtualItems, ...breathVirtualItems];
  }, [activitiesQuery.data, breathProgramsQuery.data, meditationProgramsQuery.data]);

  const shown = useMemo(() => {
    const items = activityPool
      .filter((a) => a && a.active && (isRituelMode ? hasRituelTag(a.tags ?? []) : !hasRituelTag(a.tags ?? [])))
      .filter((a) => (isRituelMode ? true : resolveTaskGroup(a) !== "diyet"));

    const normalizedGeneralKeys = new Set(["genel", "general"].map(normalizeTag));
    const wanted = new Set(karmicCodes.map((code) => getKarmicDebtTag(code)).map(normalizeTag));

    const personalized: WellnessActivity[] = [];
    const general: WellnessActivity[] = [];

    for (const a of items) {
      const tagKeys = new Set((a.tags ?? []).filter(Boolean).map(normalizeTag));

      if (wanted.size) {
        for (const w of wanted) {
          if (tagKeys.has(w)) {
            personalized.push(a);
            break;
          }
        }
      } else {
        for (const g of normalizedGeneralKeys) {
          if (tagKeys.has(g)) {
            general.push(a);
            break;
          }
        }
      }
    }

    const base = wanted.size
      ? personalized.length
        ? personalized
        : general.length
          ? general
          : items
      : general.length
        ? general
        : items;

    const q = query.trim().toLocaleLowerCase("tr-TR");
    if (!q) return base;

    return base.filter((a) => {
      const hay = `${a.title} ${a.description} ${a.metaLabel ?? ""} ${a.metaValue ?? ""} ${(a.tags ?? []).join(" ")}`.toLocaleLowerCase("tr-TR");
      return hay.includes(q);
    });
  }, [activityPool, isRituelMode, karmicCodes, query]);

  const activityById = useMemo(() => {
    const map = new Map<string, WellnessActivity>();
    for (const item of activityPool) {
      if (item?.id) map.set(item.id, item);
    }

    const meditationPrograms = meditationProgramsQuery.data ?? [];
    const breathPrograms = breathProgramsQuery.data ?? [];
    const dietPrograms = dietProgramsQuery.data ?? [];
    const ritualPrograms = (activitiesQuery.data ?? []) as ProgramItem[];

    for (const circleTask of circleTasksQuery.data ?? []) {
      const sourceProgram =
        circleTask.kind === "meditasyon"
          ? findProgramByTitle(meditationPrograms, circleTask.title)
          : circleTask.kind === "nefes"
            ? findProgramByTitle(breathPrograms, circleTask.title)
            : circleTask.kind === "diyet"
              ? findProgramByTitle(dietPrograms, circleTask.title)
              : circleTask.kind === "rituel"
                ? findProgramByTitle(ritualPrograms, circleTask.title)
                : null;

      const imageFromTask = extractImageFromTaskDescription(circleTask.description);
      const imageUrl = toPublicUrl(sourceProgram?.coverImageUrl || imageFromTask);
      const audioUrl = toPublicUrl(sourceProgram?.audioUrl);
      const iconKey = circleTask.kind === "nefes" ? "breath" : circleTask.kind === "meditasyon" ? "meditation" : sourceProgram?.iconKey;

      map.set(circleTask.id, {
        id: circleTask.id,
        title: circleTask.title,
        description: circleTask.description,
        iconKey,
        coverImageUrl: imageUrl || undefined,
        audioUrl: audioUrl || undefined,
        active: true,
        tags: [`gorev_tur:${circleTask.kind}`, `cember:${circleTask.circleId}`],
      });
    }

    return map;
  }, [activitiesQuery.data, activityPool, breathProgramsQuery.data, circleTasksQuery.data, dietProgramsQuery.data, meditationProgramsQuery.data]);

  const todayKey = useMemo(() => formatLocalDateKey(new Date()), []);


  const isCompletedToday = useCallback(
    (activityId: string) => {
      const list = progress.completedByDate[todayKey] ?? [];
      return list.includes(activityId);
    },
    [progress.completedByDate, todayKey],
  );

  const todayCompletedIds = useMemo(() => {
    return new Set(progress.completedByDate[todayKey] ?? []);
  }, [progress.completedByDate, todayKey]);

  const todayTasks = useMemo(() => {
    let source = isRituelMode
      ? activityPool.filter((item) => item && item.active && hasRituelTag(item.tags ?? []))
      : activityPool.filter((item) => item && item.active);

    if (!isRituelMode) {
      const sourceById = new Map(source.map((item) => [item.id, item] as const));

      for (const item of meditationProgramsQuery.data ?? []) {
        if (!item || !(item.active ?? true)) continue;
        if (!programDayTracking.startedAtByItemId[item.id]) continue;

        sourceById.set(item.id, {
          id: item.id,
          title: item.title,
          description: item.description?.trim() || "Meditasyon programını burada başlatıp günlük görevi tamamlayabilirsiniz.",
          iconKey: "meditation",
          coverImageUrl: item.coverImageUrl,
          audioUrl: item.audioUrl,
          durationMinutes: Number.isFinite(Number(item.durationMinutes)) && Number(item.durationMinutes) > 0 ? Number(item.durationMinutes) : undefined,
          active: true,
          tags: Array.from(new Set([...(item.tags ?? []), "gorev_tur:meditasyon", `gorev_hedef:${item.id}`])),
        } as WellnessActivity);
      }

      for (const item of breathProgramsQuery.data ?? []) {
        if (!item || !(item.active ?? true)) continue;
        if (!programDayTracking.startedAtByItemId[item.id]) continue;

        sourceById.set(item.id, {
          id: item.id,
          title: item.title,
          description: item.description?.trim() || "Nefes çalışmasını burada başlatıp günlük görevi tamamlayabilirsiniz.",
          iconKey: "breath",
          coverImageUrl: item.coverImageUrl,
          audioUrl: item.audioUrl,
          durationMinutes: Number.isFinite(Number(item.durationMinutes)) && Number(item.durationMinutes) > 0 ? Number(item.durationMinutes) : undefined,
          active: true,
          tags: Array.from(new Set([...(item.tags ?? []), "gorev_tur:nefes", `gorev_hedef:${item.id}`])),
        } as WellnessActivity);
      }

      source = Array.from(sourceById.values());
    }

    const baseTasks = source
      .map((activity) => {
        const group = resolveTaskGroup(activity);
        if (!group) return null;

        const parsedTargetId = parseActivityTargetId(activity.tags ?? [], group);
        const targetId = parsedTargetId || activity.id;
        const startedAt = programDayTracking.startedAtByItemId[targetId] ?? programDayTracking.startedAtByItemId[activity.id];

        const requiresProgramStart = group === "meditasyon" || group === "nefes" || group === "rituel" || group === "cember";
        if (requiresProgramStart && !startedAt) return null;

        const requiredDay = parseActivityProgramDay(activity.tags ?? []);
        const shouldApplyRequiredDayFilter = group !== "meditasyon" && group !== "nefes";
        if (shouldApplyRequiredDayFilter && requiredDay > 0) {
          if (!startedAt) {
            if (requiredDay !== 1) return null;
          } else {
            const currentDay = resolveProgramDay(startedAt, todayKey);
            if (currentDay !== requiredDay) return null;
          }
        }

        const taskPlayable = isPlayableTask(activity);
        const taskAudioUrl = String(activity.audioUrl ?? "").trim();
        const audioRequired = taskPlayable && Boolean(taskAudioUrl);
        const taskDone = todayCompletedIds.has(activity.id);
        const task: TodayTaskItem = {
          id: activity.id,
          title: activity.title,
          description: activity.description,
          coverImageUrl: activity.coverImageUrl,
          audioUrl: taskAudioUrl || undefined,
          group,
          targetId,
          playable: taskPlayable,
          audioRequired,
          started: Boolean(startedAt),
          done: audioRequired ? Boolean(startedAt) && taskDone : taskDone,
        };
        return task;
      })
      .filter((item): item is TodayTaskItem => Boolean(item));

    const circleTasks = isRituelMode
      ? []
      : (circleTasksQuery.data ?? []).map((task) => {
          const virtual = activityById.get(task.id);
          const taskAudioUrl = String(virtual?.audioUrl ?? "").trim();
          const audioRequired = Boolean(taskAudioUrl) && (task.kind === "meditasyon" || task.kind === "nefes");

          return {
            id: task.id,
            title: task.title,
            description: task.description,
            coverImageUrl: virtual?.coverImageUrl,
            audioUrl: taskAudioUrl || undefined,
            group: "cember",
            targetId: task.circleId,
            playable: true,
            audioRequired,
            started: true,
            done: Boolean(task.completed),
            circleName: task.circleName,
            circleTaskKind: task.kind,
            circleId: task.circleId,
            circleTaskId: task.rawTaskId,
            points: task.points,
          } satisfies TodayTaskItem;
        });

    return [...baseTasks, ...circleTasks];
  }, [
    activityById,
    activityPool,
    breathProgramsQuery.data,
    circleTasksQuery.data,
    isRituelMode,
    meditationProgramsQuery.data,
    programDayTracking.startedAtByItemId,
    todayCompletedIds,
    todayKey,
  ]);

  const filteredTodayTasks = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr-TR");
    if (!q) return todayTasks;

    return todayTasks.filter((task) => {
      const hay = `${task.title} ${task.description} ${TASK_GROUP_LABELS[task.group]}`.toLocaleLowerCase("tr-TR");
      return hay.includes(q);
    });
  }, [query, todayTasks]);

  const groupedTodayTasks = useMemo(() => {
    return TASK_GROUP_ORDER.map((groupKey) => ({
      groupKey,
      title: TASK_GROUP_LABELS[groupKey],
      items: filteredTodayTasks.filter((task) => task.group === groupKey),
    })).filter((section) => section.items.length > 0);
  }, [filteredTodayTasks]);

  const programProgressByTaskKey = useMemo(() => {
    const map = new Map<string, { currentDay: number; totalDays: number; completedDays: number }>();

    const build = (args: {
      group: TaskGroupKey;
      items: ProgramItem[];
      getProgramDays: (item: ProgramItem) => number;
      filter?: (item: ProgramItem) => boolean;
    }) => {
      for (const item of args.items ?? []) {
        if (!item || !(item.active ?? true)) continue;
        if (args.filter && !args.filter(item)) continue;

        let totalDays = args.getProgramDays(item);

        const tasksForProgram = todayTasks.filter(
          (task) => task.group === args.group && (task.targetId === item.id || task.id === item.id),
        );

        const startedAt =
          programDayTracking.startedAtByItemId[item.id] ??
          tasksForProgram
            .map((task) => programDayTracking.startedAtByItemId[task.targetId] ?? programDayTracking.startedAtByItemId[task.id])
            .find(Boolean);
        if (!startedAt) continue;

        if (totalDays <= 0) {
          const fromActivityPool = activityPool.find((activity) => activity.id === item.id);
          if (fromActivityPool) {
            totalDays = resolveProgramDaysFromItem(fromActivityPool as ProgramItem);
          }
        }

        const rawCurrentDay = resolveProgramDay(startedAt, todayKey);
        if (totalDays <= 0 && (args.group === "meditasyon" || args.group === "nefes") && tasksForProgram.length > 0) {
          totalDays = 7;
        }

        if (totalDays <= 0) continue;
        const currentDay = Math.max(1, Math.min(totalDays, rawCurrentDay));
        const todayDoneForProgram = tasksForProgram.length > 0 && tasksForProgram.every((task) => task.done);
        const completedDays = Math.min(totalDays, Math.max(0, currentDay - 1 + (todayDoneForProgram ? 1 : 0)));

        const progress = {
          currentDay,
          totalDays,
          completedDays,
        };

        map.set(`${args.group}:${item.id}`, progress);
        for (const task of tasksForProgram) {
          map.set(`${args.group}:${task.targetId}`, progress);
          map.set(`${args.group}:${task.id}`, progress);
        }
      }
    };

    const meditationItems = meditationProgramsQuery.data ?? [];
    const breathItems = breathProgramsQuery.data ?? [];
    const dietItems = dietProgramsQuery.data ?? [];
    const activityItems = activitiesQuery.data ?? [];

    const isBreath = (item: ProgramItem) => {
      const tags = new Set((item.tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean));
      return tags.has("nefes") || tags.has("breath");
    };

    const isMeditation = (item: ProgramItem) => {
      const tags = new Set((item.tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean));
      return !isBreath(item) && !hasRituelTag(item.tags ?? []);
    };

    const isRituel = (item: ProgramItem) => {
      return hasRituelTag(item.tags ?? []) || parseRitualProgramDaysFromTags(item.tags ?? []) > 0;
    };

    const isCember = (item: ProgramItem) => resolveTaskGroup(item as WellnessActivity) === "cember";

    build({ group: "diyet", items: dietItems, getProgramDays: (item) => parseProgramDaysFromTags(item.tags ?? []) });
    build({ group: "meditasyon", items: meditationItems, getProgramDays: resolveProgramDaysFromItem, filter: isMeditation });
    build({ group: "nefes", items: breathItems, getProgramDays: resolveProgramDaysFromItem, filter: isBreath });
    build({ group: "rituel", items: activityItems, getProgramDays: (item) => parseRitualProgramDaysFromTags(item.tags ?? []), filter: isRituel });
    build({ group: "cember", items: activityItems, getProgramDays: (item) => parseProgramDaysFromTags(item.tags ?? []), filter: isCember });

    return map;
  }, [activityPool, activitiesQuery.data, breathProgramsQuery.data, dietProgramsQuery.data, meditationProgramsQuery.data, programDayTracking.startedAtByItemId, todayKey, todayTasks]);

  const doneTasks = useMemo(() => todayTasks.filter((task) => task.done).length, [todayTasks]);
  const totalTasks = todayTasks.length;
  const completedRatio = totalTasks > 0 ? doneTasks / totalTasks : 0;

  const ensureProgramStart = useCallback(
    (itemId: string) => {
      setProgramDayTracking((prev) => {
        if (prev.startedAtByItemId[itemId]) return prev;
        const next = {
          startedAtByItemId: {
            ...prev.startedAtByItemId,
            [itemId]: todayKey,
          },
        };
        saveProgramDayTracking(userKey, next);
        return next;
      });
    },
    [todayKey, userKey],
  );

  const cancelProgramStart = useCallback(
    (itemId: string) => {
      setProgramDayTracking((prev) => {
        if (!prev.startedAtByItemId[itemId]) return prev;
        const nextStartedAt = { ...prev.startedAtByItemId };
        delete nextStartedAt[itemId];
        const next = { startedAtByItemId: nextStartedAt };
        saveProgramDayTracking(userKey, next);
        return next;
      });

      setActiveId((prev) => (prev === itemId ? null : prev));
    },
    [userKey],
  );

  const productById = useMemo(() => {
    const map = new Map<string, ShopProduct>();
    for (const product of productsQuery.data ?? []) {
      if (product?.id && product.active) map.set(product.id, product);
    }
    return map;
  }, [productsQuery.data]);

  const reportTaskCompletion = useCallback(
    (activity: WellnessActivity, group: TaskGroupKey | null, programDay: number) => {
      const taskPoints = parseActivityTaskPoints(activity);
      const practiceEventType = group === "nefes" ? "breath_completed" : group === "meditasyon" ? "meditation_completed" : null;
      const durationMinutesRaw = Number(activity.durationMinutes ?? 0);
      const durationMinutes = Number.isFinite(durationMinutesRaw) && durationMinutesRaw > 0 ? Math.round(durationMinutesRaw * 100) / 100 : 0;

      void sendProgressEvent({
        eventType: "task_completed",
        dateKey: todayKey,
        sourceId: activity.id,
        metadata: {
          kind: group ?? "ozel",
          task_category: group === "rituel" ? "ritual" : group === "diyet" ? "diet" : group == null ? "challenge" : "activity",
          points: taskPoints,
          taskPoints,
          taskTitle: activity.title,
          programDay,
        },
      })
        .then(async () => {
          if (practiceEventType) {
            await sendProgressEvent({
              eventType: practiceEventType,
              dateKey: todayKey,
              sourceId: activity.id,
              metadata: {
                mode: practiceEventType === "breath_completed" ? "breath" : "meditation",
                title: activity.title,
                durationMinutes,
              },
            }).catch(() => null);
          }

          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["progress-summary", user?.id] }),
            queryClient.invalidateQueries({ queryKey: ["progress-timeline", user?.id] }),
          ]);
        })
        .catch(() => {
          // non-blocking progress logging
        });
    },
    [queryClient, todayKey, user?.id],
  );

  const resetAudioPlaybackTracking = useCallback((activityId: string) => {
    delete audioPlaybackRef.current[activityId];
  }, []);

  const trackAudioPlaybackProgress = useCallback((activityId: string, currentTime: number, duration: number) => {
    if (!activityId) return;
    if (!Number.isFinite(currentTime) || currentTime < 0) return;

    const now = Date.now() / 1_000;
    const prev = audioPlaybackRef.current[activityId];
    if (!prev) {
      audioPlaybackRef.current[activityId] = {
        durationSeconds: Number.isFinite(duration) && duration > 0 ? duration : 0,
        listenedSeconds: 0,
        lastCurrentTime: currentTime,
        lastWallTime: now,
      };
      return;
    }

    const currentDuration = Number.isFinite(duration) && duration > 0 ? duration : prev.durationSeconds;
    const timeDelta = currentTime - prev.lastCurrentTime;
    const wallDelta = now - prev.lastWallTime;
    const listenedDelta = timeDelta > 0 && timeDelta <= 2.2 && wallDelta > 0 && wallDelta <= 2.5 ? timeDelta : 0;

    audioPlaybackRef.current[activityId] = {
      durationSeconds: currentDuration,
      listenedSeconds: prev.listenedSeconds + listenedDelta,
      lastCurrentTime: currentTime,
      lastWallTime: now,
    };
  }, []);

  const hasEnoughAudioPlayback = useCallback((activityId: string) => {
    const tracked = audioPlaybackRef.current[activityId];
    if (!tracked) return false;

    const duration = Number(tracked.durationSeconds);
    if (!Number.isFinite(duration) || duration <= 0) return false;

    const requiredSeconds = Math.max(8, duration * 0.85);
    return tracked.listenedSeconds >= requiredSeconds;
  }, []);

  const onStartOrComplete = useCallback(
    (activityId: string, options?: { fromAudioEnded?: boolean; audioCompletionEligible?: boolean }) => {
      const activity = activityById.get(activityId);
      const fromAudioEnded = options?.fromAudioEnded === true;
      const audioCompletionEligible = options?.audioCompletionEligible !== false;
      const requiresAudioCompletion = Boolean(activity) && Boolean(String(activity.audioUrl ?? "").trim()) && isPlayableTask(activity);
      const ritualConfig = isRituelMode ? parseRituelConfigFromTags(activity?.tags ?? []) : null;
      const ritualPoints = ritualConfig?.points ?? 0;

      if (isRituelMode && (ritualConfig?.programDays ?? 0) > 0 && !programDayTracking.startedAtByItemId[activityId]) {
        toast({
          title: "Önce programı başlat",
          description: "Ritüel görevlerini başlatmadan önce programı aktif etmelisiniz.",
        });
        return;
      }

      if (isCompletedToday(activityId)) {
        toggleCompleteToday(activityId);
        resetAudioPlaybackTracking(activityId);
        if (isRituelMode) {
          setRitualRewards((prev) => {
            const dayRewards = { ...(prev.rewardsByDate[todayKey] ?? {}) };
            delete dayRewards[activityId];
            const next = {
              rewardsByDate: {
                ...prev.rewardsByDate,
                [todayKey]: dayRewards,
              },
            };
            saveRitualRewards(userKey, next);
            return next;
          });
        }
        setActiveId((prev) => (prev === activityId ? null : prev));
        return;
      }

      if (activeId === activityId) {
        if (requiresAudioCompletion && !fromAudioEnded) {
          toast({
            title: "Dinleme tamamlanmadı",
            description: "Bu görevi tamamlamak için ses kaydı bitene kadar dinlemelisiniz.",
          });
          return;
        }

        if (requiresAudioCompletion && fromAudioEnded && !audioCompletionEligible) {
          toast({
            title: "Dinleme yetersiz",
            description: "Görevin tamamlanması için sesi ileri sarmadan büyük kısmını dinlemelisiniz.",
          });
          setActiveId(null);
          resetAudioPlaybackTracking(activityId);
          return;
        }

        ensureProgramStart(activityId);
        toggleCompleteToday(activityId);
        if (isRituelMode && ritualPoints > 0) {
          setRitualRewards((prev) => {
            const dayRewards = { ...(prev.rewardsByDate[todayKey] ?? {}) };
            dayRewards[activityId] = ritualPoints;
            const next = {
              rewardsByDate: {
                ...prev.rewardsByDate,
                [todayKey]: dayRewards,
              },
            };
            saveRitualRewards(userKey, next);
            return next;
          });
        }
        setActiveId(null);
        resetAudioPlaybackTracking(activityId);

        const completedActivity = activityById.get(activityId);
        if (completedActivity) {
          const completedGroup = resolveTaskGroup(completedActivity);
          const completedTargetId = completedGroup
            ? parseActivityTargetId(completedActivity.tags ?? [], completedGroup) || completedActivity.id
            : completedActivity.id;
          const startedAt =
            programDayTracking.startedAtByItemId[completedTargetId] ??
            programDayTracking.startedAtByItemId[completedActivity.id];
          const programDay = startedAt ? resolveProgramDay(startedAt, todayKey) : 1;
          reportTaskCompletion(completedActivity, completedGroup, programDay);
        }

        if (user?.id && completedActivity) {
          const postBody = isRituelMode
            ? `Bugünkü ritüelimi tamamladım: ${completedActivity.title}`
            : `Bugünkü aktivitemi tamamladım: ${completedActivity.title}`;

          void createSocialPost({ body: postBody })
            .then(async () => {
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user.id] }),
                queryClient.invalidateQueries({ queryKey: ["social-feed-activities", user.id] }),
                queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user.id] }),
              ]);
            })
            .catch(() => {
              // automatic sharing failure should not block completion flow
            });
        }

        toast({
          title: isRituelMode ? "Ritüel tamamlandı" : "Aktivite tamamlandı",
          description: "Tamamlanan adım kaydedildi.",
        });
        return;
      }

      ensureProgramStart(activityId);
      resetAudioPlaybackTracking(activityId);
      setActiveId(activityId);
    },
    [
      activeId,
      activityById,
      ensureProgramStart,
      isCompletedToday,
      isRituelMode,
      programDayTracking.startedAtByItemId,
      queryClient,
      reportTaskCompletion,
      resetAudioPlaybackTracking,
      toast,
      todayKey,
      toggleCompleteToday,
      user?.id,
      userKey,
    ],
  );

  const toggleCircleTaskFromActivity = useCallback(
    async (task: TodayTaskItem, options?: { fromAudioEnded?: boolean; stopAudio?: boolean }) => {
      if (!task.circleId || !task.circleTaskId) return;

      if (task.audioRequired) {
        const fromAudioEnded = options?.fromAudioEnded === true;
        const stopAudio = options?.stopAudio === true;

        if (stopAudio) {
          setActiveId((prev) => (prev === task.id ? null : prev));
          resetAudioPlaybackTracking(task.id);
          return;
        }

        if (!task.done && !fromAudioEnded) {
          setActiveId(task.id);
          return;
        }
      }

      const completion = await toggleCircleTaskCompletionInDb({
        circleId: task.circleId,
        taskId: task.circleTaskId,
        dateKey: todayKey,
      });

      if (completion.completed) {
        const taskPoints = mapCircleTaskPointsToXp(task.points ?? 1);
        await sendProgressEvent({
          eventType: "task_completed",
          dateKey: todayKey,
          sourceId: `circle:${task.circleId}:${task.circleTaskId}:${todayKey}`,
          metadata: {
            source: "circle_task",
            circleId: task.circleId,
            taskId: task.circleTaskId,
            taskKind: task.circleTaskKind ?? "cember",
            title: task.title,
            taskPoints,
            rawTaskPoints: task.points ?? 1,
          },
        }).catch(() => undefined);
      }

      setActiveId((prev) => (prev === task.id ? null : prev));
      resetAudioPlaybackTracking(task.id);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["panel-activity-circle-tasks", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["circle-task-counts", task.circleId] }),
        queryClient.invalidateQueries({ queryKey: ["circle-activity-feed", task.circleId] }),
        queryClient.invalidateQueries({ queryKey: ["circle-tasks-preview", task.circleId, user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["progress-summary", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["progress-timeline", user?.id] }),
      ]);
    },
    [queryClient, resetAudioPlaybackTracking, todayKey, user?.id],
  );

  const onTodayTaskClick = useCallback(
    (task: TodayTaskItem, options?: { fromAudioEnded?: boolean; stopAudio?: boolean }) => {
      if (task.group === "cember") {
        void toggleCircleTaskFromActivity(task, options);
        return;
      }

      if (task.playable) {
        const fromAudioEnded = options?.fromAudioEnded === true;
        const stopAudio = options?.stopAudio === true;

        if (task.audioRequired) {
          if (stopAudio) {
            setActiveId((prev) => (prev === task.id ? null : prev));
            resetAudioPlaybackTracking(task.id);
            return;
          }

          if (fromAudioEnded) {
            onStartOrComplete(task.id, {
              fromAudioEnded: true,
              audioCompletionEligible: hasEnoughAudioPlayback(task.id),
            });
            return;
          }

          if (task.done) {
            onStartOrComplete(task.id);
            return;
          }

          if (activeId !== task.id) {
            onStartOrComplete(task.id);
          }
          return;
        }

        onStartOrComplete(task.id, options);
        return;
      }

      const completingNow = !task.done;
      toggleCompleteToday(task.id);
      if (activeId === task.id) {
        setActiveId(null);
      }

      if (completingNow) {
        const completedActivity = activityById.get(task.id);
        if (completedActivity) {
          const startedAt =
            programDayTracking.startedAtByItemId[task.targetId] ??
            programDayTracking.startedAtByItemId[task.id];
          const programDay = startedAt ? resolveProgramDay(startedAt, todayKey) : 1;
          reportTaskCompletion(completedActivity, task.group, programDay);
        }
      }
    },
    [activeId, activityById, hasEnoughAudioPlayback, onStartOrComplete, programDayTracking.startedAtByItemId, reportTaskCompletion, resetAudioPlaybackTracking, todayKey, toggleCircleTaskFromActivity, toggleCompleteToday],
  );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <ProfileOverviewHeader />
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">{isRituelMode ? "Kişiye Özel Ritüeller" : "Kişiye Özel Aktiviteler"}</div>
          <div className="mt-2 flex items-center gap-2 text-slate-600 dark:text-slate-400">
            <Sparkles className="h-5 w-5 text-[var(--panel-primary)]" />
            <div className="text-sm">
              Bugünün numeroloji sayısı <span className="font-bold text-[var(--panel-primary)]">{daily.number}</span>: {daily.focus}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-slate-900 px-5 py-3 text-xs font-extrabold text-white shadow-sm dark:bg-white dark:text-slate-900">
          {isRituelMode ? "Ritüeller yönetim panelinden eklenir" : "Aktiviteler yönetim panelinden eklenir"}
        </div>
      </header>

      <section>
        <div className="rounded-3xl border border-[#d9c7d8] bg-gradient-to-br from-[#f7ecf6] via-[#f3e6f0] to-[#efe1ed] p-6 shadow-xl sm:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[var(--panel-primary)]">
                Günlük İlerleme
              </div>
              <div className="mt-3 text-3xl font-black text-slate-900 dark:text-white">%{Math.round(completedRatio * 100)}</div>
              <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">Tamamlanan görev: {doneTasks}/{totalTasks || 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-300 bg-white/80 px-4 py-3 text-right">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bugün</div>
              <div className="text-lg font-black text-[var(--panel-primary)]">{doneTasks}/{totalTasks || 0} görev</div>
            </div>
          </div>

          <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-white/70">
            <div className="h-full rounded-full bg-[var(--panel-primary)] transition-all" style={{ width: `${Math.round(completedRatio * 100)}%` }} />
          </div>

        </div>
      </section>

      <section className="grid grid-cols-1 gap-6">
        <div className="space-y-4">
          <div className="flex justify-end">
            <div className="w-full max-w-sm">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={isRituelMode ? "Ritüel ara..." : "Aktivite ara..."}
                className="h-11 rounded-2xl bg-white shadow-sm dark:bg-slate-900"
              />
            </div>
          </div>

          <div className="pt-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-[var(--panel-primary)]/10 dark:bg-slate-900/40">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-xl font-extrabold text-slate-900 dark:text-white">Bugünün Görevleri</div>
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">Diyet, meditasyon, nefes, ritüel ve çember görevlerin tek merkezde.</div>
                </div>
                <div className="rounded-full bg-[var(--panel-primary)]/10 px-3 py-1 text-xs font-black text-[var(--panel-primary)]">
                  {doneTasks}/{totalTasks || 0}
                </div>
              </div>

              <div className="mt-5 space-y-5">
                {groupedTodayTasks.length ? (
                  groupedTodayTasks.map((section) => (
                    <div key={`task-group-${section.groupKey}`} className="space-y-3">
                      <div className="text-sm font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">{section.title}</div>
                      <div className="space-y-2">
                        {(section.groupKey === "cember"
                          ? [...section.items].sort((a, b) => `${a.targetId}:${a.title}`.localeCompare(`${b.targetId}:${b.title}`, "tr-TR"))
                          : section.items
                        ).map((task, index, allItems) => {
                          const isActive = activeId === task.id;
                          const miniProgress = programProgressByTaskKey.get(`${task.group}:${task.targetId}`);
                          const miniProgressRatio = miniProgress ? Math.round((miniProgress.currentDay / Math.max(1, miniProgress.totalDays)) * 100) : 0;
                          const showCircleHeader = section.groupKey === "cember" && (index === 0 || allItems[index - 1]?.targetId !== task.targetId);
                          const circleName = section.groupKey === "cember" ? task.circleName ?? "Çember" : "";
                          const parsedDescription = parseTaskDescriptionForSteps(task.description);
                          const showStepList = parsedDescription.steps.length > 0 && (task.group === "cember" || task.circleTaskKind === "diyet");

                          return (
                            <div key={task.id} className="space-y-1">
                              {showCircleHeader ? (
                                <div className="rounded-xl border border-[var(--panel-primary)]/20 bg-[var(--panel-primary)]/5 px-3 py-2 text-xs font-bold text-[var(--panel-primary)]">
                                  {circleName}
                                </div>
                              ) : null}
                              <div
                              onClick={() => (task.playable ? undefined : onTodayTaskClick(task))}
                              className={cn(
                                "rounded-2xl border p-3 transition",
                                task.done
                                  ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/30 dark:bg-emerald-500/10"
                                  : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/30",
                                !task.playable && "cursor-pointer hover:border-[var(--panel-primary)]/35",
                              )}
                            >
                              <div className="flex items-start gap-3">
                                {task.coverImageUrl ? (
                                  <img
                                    src={toPublicUrl(task.coverImageUrl)}
                                    alt={task.title}
                                    className={cn(
                                      "rounded-xl object-cover",
                                      task.audioRequired ? "h-40 w-full rounded-2xl sm:h-24 sm:w-40" : "h-16 w-16",
                                    )}
                                  />
                                ) : (
                                  <div
                                    className={cn(
                                      "grid place-items-center rounded-xl bg-slate-100 text-slate-400 dark:bg-slate-800",
                                      task.audioRequired ? "h-40 w-full rounded-2xl sm:h-24 sm:w-40" : "h-16 w-16",
                                    )}
                                  >
                                    <Circle className="h-4 w-4" />
                                  </div>
                                )}

                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                                    {task.done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-400" />}
                                    <span className={cn(task.done && "line-through text-slate-500 dark:text-slate-400")}>{task.title}</span>
                                  </div>
                                  {parsedDescription.summary ? (
                                    <div className={cn("mt-1 text-xs text-slate-600 dark:text-slate-400", task.group === "cember" && "whitespace-pre-line")}>{parsedDescription.summary}</div>
                                  ) : null}
                                  {showStepList ? (
                                    <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-slate-600 dark:text-slate-400">
                                      {parsedDescription.steps.map((step) => (
                                        <li key={`${task.id}-${step}`}>{step}</li>
                                      ))}
                                    </ul>
                                  ) : !parsedDescription.summary ? (
                                    <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">Açıklama eklenmedi.</div>
                                  ) : null}

                                  {miniProgress ? (
                                    <div className="mt-3 rounded-xl border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/5 px-2 py-2">
                                      <div className="flex items-center justify-between text-[10px] font-black text-[var(--panel-primary)]">
                                        <span>{miniProgress.currentDay}/{miniProgress.totalDays}. gün</span>
                                        <span>{miniProgress.currentDay}. gün</span>
                                      </div>
                                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/80">
                                        <div className="h-full rounded-full bg-[var(--panel-primary)] transition-all" style={{ width: `${miniProgressRatio}%` }} />
                                      </div>
                                    </div>
                                  ) : null}

                                  {task.playable && task.audioRequired && isActive && !task.done ? (
                                    <div className="mt-3 overflow-hidden rounded-xl border border-[var(--panel-primary)]/20 bg-[var(--panel-primary)]/5 p-2">
                                      <audio
                                        src={toPublicUrl(task.audioUrl)}
                                        controls
                                        autoPlay
                                        preload="metadata"
                                        className="w-full"
                                        onLoadedMetadata={(event) => {
                                          trackAudioPlaybackProgress(task.id, 0, event.currentTarget.duration);
                                        }}
                                        onTimeUpdate={(event) => {
                                          trackAudioPlaybackProgress(task.id, event.currentTarget.currentTime, event.currentTarget.duration);
                                        }}
                                        onEnded={() => onTodayTaskClick(task, { fromAudioEnded: true })}
                                      />
                                      <div className="mt-2 text-[10px] font-bold text-[var(--panel-primary)]/90">Ses kaydı bitince görev otomatik tamamlanır.</div>
                                    </div>
                                  ) : null}
                                </div>

                                {task.playable ? (
                                  <button
                                    type="button"
                                    onClick={() => onTodayTaskClick(task, task.audioRequired && isActive && !task.done ? { stopAudio: true } : undefined)}
                                    className={cn(
                                      "inline-flex items-center justify-center rounded-full text-xs font-extrabold",
                                      task.audioRequired ? "self-end h-10 w-10 border border-[var(--panel-primary)]/35 bg-[var(--panel-primary)]/10 text-[var(--panel-primary)]" : "gap-2 px-4 py-2",
                                      task.done
                                        ? "border-0 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
                                        : isActive
                                          ? "border-0 bg-[var(--panel-primary)] text-white"
                                          : !task.audioRequired
                                            ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                                            : "",
                                    )}
                                  >
                                    {task.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : task.audioRequired ? (isActive ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />) : <ArrowRight className="h-3.5 w-3.5" />}
                                    {!task.audioRequired ? (task.done ? "Tamamlandı" : "Tamamla") : null}
                                  </button>
                                ) : (
                                  <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400">{task.done ? "Tamamlandı" : "Dokun tamamla"}</div>
                                )}
                              </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    Başlatılmış programlara ait bugünlük görev bulunamadı.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </section>
    </div>
  );
}
